import type {
  LessonCallDetail,
  TranscriptFragment,
} from "@repo/app-types/client/client";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { TrainingParticipant } from "@repo/app-types/entities/trainingParticipant";
import type { TrainingParticipantChapterGradingApi } from "@repo/app-types/entities/trainingParticipantChapterGrading";
import {
  getTagsInvalidatedByDelete,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const TrainingParticipantTagType = "TrainingParticipant";
type TrainingParticipantTagType = typeof TrainingParticipantTagType;

const trainingParticipantApi = api
  .enhanceEndpoints({
    addTagTypes: [TrainingParticipantTagType],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      allTrainingParticipantsForProject: builder.infiniteQuery<
        {
          participants: TrainingParticipant[];
          nextCursor: string | null;
        },
        { projectId: string; limit?: number },
        string | null,
        {
          data: { training_participants: TrainingParticipant[] };
          next_cursor: string | null;
        }
      >({
        infiniteQueryOptions: {
          initialPageParam: null,
          getPreviousPageParam: () => null,
          getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
        query: ({ queryArg: { projectId, limit }, pageParam }) => ({
          method: "GET",
          url: `/training/${encodeURIComponent(projectId)}/participants`,
          params: {
            // Only supported sort, for now
            sort: "-last_activity_at",
            limit: limit ?? undefined,
            cursor: pageParam ?? undefined,
          },
        }),
        transformResponse: (response) => ({
          participants: response.data.training_participants,
          nextCursor: response.next_cursor,
        }),
        providesTags: (result, error, { projectId }) =>
          getTagsProvidedByList(
            TrainingParticipantTagType,
            result?.pages.flatMap((page) => page.participants),
            { filteredBy: [projectId] },
          ),
      }),

      allTrainingParticipantsForPlaybookTeammate: builder.query<
        TrainingParticipant[],
        { playbookId: PlaybookId; teammateId: TeammateId },
        { data: { training_participants: TrainingParticipant[] } }
      >({
        query: ({ playbookId, teammateId }) => ({
          method: "GET",
          url: `/performance/playbooks/${encodeURIComponent(
            playbookId,
          )}/teammates/${encodeURIComponent(teammateId)}/trainings`,
        }),
        transformResponse: (response) => response.data.training_participants,
      }),

      generateInterviewUrlForTrainingParticipant: builder.mutation<
        { interviewUrl: string },
        { participantId: string },
        { interview_url: string }
      >({
        query: ({ participantId }) => ({
          method: "POST",
          url: `/teammates/generate-training-interview-url`,
          body: {
            participant_id: participantId,
          },
        }),
        transformResponse: (response) => ({
          interviewUrl: response.interview_url,
        }),
      }),

      trainingParticipant: builder.query<
        TrainingParticipant,
        { projectId: string; participantId: string },
        { data: { training_participant: TrainingParticipant } }
      >({
        query: ({ projectId, participantId }) => ({
          method: "GET",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}`,
        }),
        transformResponse: (response) => response.data.training_participant,
        providesTags: (result, error, { participantId }) =>
          getTagsProvidedByEntity(TrainingParticipantTagType, participantId),
      }),

      trainingParticipantLessonAttempts: builder.query<
        { id: string; started_at: string }[],
        {
          projectId: string;
          participantId: string;
          interviewSectionDefinitionId: string;
        },
        { data: { attempts: { id: string; started_at: string }[] } }
      >({
        query: ({
          projectId,
          participantId,
          interviewSectionDefinitionId,
        }) => ({
          method: "GET",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}/lessons/${encodeURIComponent(interviewSectionDefinitionId)}/attempts`,
        }),
        transformResponse: (response) => response.data.attempts,
      }),

      trainingParticipantLessonTranscript: builder.query<
        | {
            voice_transcript_fragments: TranscriptFragment[];
          }
        | {
            chat_messages: {
              id: string;
              role: "user" | "assistant";
              content: string;
              created_at: string;
            }[];
          },
        {
          projectId: string;
          participantId: string;
          interviewSectionDefinitionId: string;
          interviewSectionResultId?: string;
        }
      >({
        query: ({
          projectId,
          participantId,
          interviewSectionDefinitionId,
          interviewSectionResultId,
        }) => ({
          method: "GET",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}/lessons/${encodeURIComponent(interviewSectionDefinitionId)}/transcript`,
          params: {
            interview_section_result_id: interviewSectionResultId,
          },
        }),
      }),

      trainingParticipantChapterGradings: builder.query<
        TrainingParticipantChapterGradingApi[],
        {
          projectId: string;
          participantId: string;
          chapterId: string;
        },
        {
          data: {
            trainings_participant_chapter_gradings: TrainingParticipantChapterGradingApi[];
          };
        }
      >({
        query: ({ projectId, participantId, chapterId }) => ({
          method: "GET",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}/chapters/${encodeURIComponent(chapterId)}/gradings`,
        }),
        transformResponse: (response) =>
          response.data.trainings_participant_chapter_gradings,
        providesTags: (result, error, { participantId }) =>
          getTagsProvidedByEntity(TrainingParticipantTagType, participantId),
      }),

      trainingParticipantLessonCallsDetails: builder.query<
        LessonCallDetail[],
        {
          projectId: string;
          participantId: string;
          interviewSectionDefinitionId: string;
          interviewSectionResultId?: string;
        },
        {
          data: {
            calls_details: LessonCallDetail[];
          };
        }
      >({
        query: ({
          projectId,
          participantId,
          interviewSectionDefinitionId,
          interviewSectionResultId,
        }) => ({
          method: "POST",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}/lessons/${encodeURIComponent(interviewSectionDefinitionId)}/calls_details`,
          body: interviewSectionResultId
            ? { interview_section_result_id: interviewSectionResultId }
            : undefined,
        }),
        transformResponse: (response) => response.data.calls_details,
      }),

      deleteTrainingParticipant: builder.mutation<
        void,
        { projectId: string; participantId: string }
      >({
        query: ({ projectId, participantId }) => ({
          method: "DELETE",
          url: `/training/${encodeURIComponent(projectId)}/participants/${encodeURIComponent(participantId)}`,
        }),
        async onQueryStarted(
          { projectId, participantId },
          { dispatch, queryFulfilled },
        ) {
          // Optimistically update the cache to remove the participant
          const patch = dispatch(
            trainingParticipantApi.util.updateQueryData(
              "allTrainingParticipantsForProject",
              { projectId },
              (draft) => {
                for (const page of draft.pages) {
                  const index = page.participants.findIndex(
                    (p) => p.id === participantId,
                  );
                  if (index !== -1) {
                    page.participants.splice(index, 1);
                    return;
                  }
                }
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patch.undo();
          }
        },
        invalidatesTags: (result, error, { participantId }) =>
          getTagsInvalidatedByDelete(TrainingParticipantTagType, participantId),
      }),
    }),
  });

export const {
  useAllTrainingParticipantsForProjectInfiniteQuery,
  useAllTrainingParticipantsForPlaybookTeammateQuery,
  useGenerateInterviewUrlForTrainingParticipantMutation,
  useTrainingParticipantQuery,
  useTrainingParticipantLessonAttemptsQuery,
  useTrainingParticipantChapterGradingsQuery,
  useTrainingParticipantLessonTranscriptQuery,
  useTrainingParticipantLessonCallsDetailsQuery,
  useDeleteTrainingParticipantMutation,
} = trainingParticipantApi;
